/**
 * PassengerIndex Service
 *
 * Provides O(1) lookups for passenger data instead of O(n) .find() operations.
 * Build indexes once when data loads, then use fast lookups everywhere.
 *
 * Usage:
 *   import { passengerIndex } from '@/services/PassengerIndex'
 *
 *   // Build indexes when week data loads
 *   passengerIndex.buildIndexes(weekData)
 *
 *   // O(1) lookup by reference
 *   const passenger = passengerIndex.getByReference('NUCO-12345')
 *
 *   // O(1) lookup passengers by transfer
 *   const passengers = passengerIndex.getByTransfer('TR-001')
 */

class PassengerIndex {
    constructor() {
        // Primary index: reference -> passenger
        this.byReference = new Map();

        // Secondary indexes: various keys -> array of passengers
        this.byTransferInbound = new Map();   // transferId -> passengers[]
        this.byTransferOutbound = new Map();  // transferId -> passengers[]
        this.byCoach = new Map();             // coachId -> passengers[]
        this.byEvent = new Map();             // eventId -> passengers[]
        this.byAccommodation = new Map();     // subblock_id -> passengers[]
        this.byLesson = new Map();            // lessonId -> passengers[]

        // Search index: pre-lowercased for fast text search
        this._searchIndex = [];  // Array of { passenger, nameLower, refLower, brand }

        // Brand index: brand -> passengers[] for permission filtering
        this.byBrand = new Map();

        // Metadata
        this.lastBuildTime = null;
        this.passengerCount = 0;
        this.buildDurationMs = 0;

        // Raw data reference (for fallback)
        this._weekData = null;
    }

    /**
     * Build all indexes from week data
     * Call this when week data is loaded/updated
     *
     * @param {Array} weekData - Array of passenger objects
     * @returns {Object} Build statistics
     */
    buildIndexes(weekData) {
        const startTime = performance.now();

        // Clear existing indexes
        this.clear();

        if (!weekData || !Array.isArray(weekData)) {
            console.warn('[PassengerIndex] Invalid weekData provided');
            return { success: false, error: 'Invalid data' };
        }

        this._weekData = weekData;
        this.passengerCount = weekData.length;

        // Build all indexes in a single pass
        for (const passenger of weekData) {
            if (!passenger) continue;

            // Primary index by reference
            if (passenger.reference) {
                this.byReference.set(passenger.reference, passenger);

                // Extract brand from reference (e.g., 'NUCO-12345' -> 'NUCO')
                const brand = passenger.reference.split('-')[0].toUpperCase();

                // Build search index entry with pre-lowercased strings
                this._searchIndex.push({
                    passenger,
                    nameLower: (passenger.passengerName || '').toLowerCase(),
                    refLower: passenger.reference.toLowerCase(),
                    brand
                });

                // Build brand index for permission filtering
                this._addToIndex(this.byBrand, brand, passenger);
            }

            // Process allocPlaces for secondary indexes
            if (passenger.allocPlaces && Array.isArray(passenger.allocPlaces)) {
                for (const alloc of passenger.allocPlaces) {
                    // Transport indexes (inbound/outbound)
                    if (alloc.transport) {
                        const transport = alloc.transport;
                        const transferId = transport.transfer_id || transport.id;
                        const coachId = transport.coach;

                        if (transport.type === 'transport-inbound' && transferId) {
                            this._addToIndex(this.byTransferInbound, transferId, passenger);
                        } else if (transport.type === 'transport-outbound' && transferId) {
                            this._addToIndex(this.byTransferOutbound, transferId, passenger);
                        }

                        if (coachId) {
                            this._addToIndex(this.byCoach, coachId, passenger);
                        }
                    }

                    // Event index
                    if (alloc.event?.id) {
                        this._addToIndex(this.byEvent, alloc.event.id, passenger);
                    }

                    // Accommodation index
                    if (alloc.accommodation?.subblock_id) {
                        this._addToIndex(this.byAccommodation, alloc.accommodation.subblock_id, passenger);
                    }

                    // Lesson index
                    if (alloc.lessons?.id) {
                        this._addToIndex(this.byLesson, alloc.lessons.id, passenger);
                    }
                }
            }
        }

        this.buildDurationMs = performance.now() - startTime;
        this.lastBuildTime = new Date();

        const stats = this.getStats();
        console.log(
            `[PassengerIndex] Built indexes for ${this.passengerCount} passengers in ${this.buildDurationMs.toFixed(1)}ms`,
            stats
        );

        return { success: true, stats };
    }

    /**
     * Helper to add passenger to a multi-value index
     */
    _addToIndex(index, key, passenger) {
        if (!index.has(key)) {
            index.set(key, []);
        }
        // Avoid duplicates (same passenger might have multiple allocs with same key)
        const existing = index.get(key);
        if (!existing.includes(passenger)) {
            existing.push(passenger);
        }
    }

    /**
     * Clear all indexes
     */
    clear() {
        this.byReference.clear();
        this.byTransferInbound.clear();
        this.byTransferOutbound.clear();
        this.byCoach.clear();
        this.byEvent.clear();
        this.byAccommodation.clear();
        this.byLesson.clear();
        this.byBrand.clear();
        this._searchIndex = [];
        this._weekData = null;
        this.passengerCount = 0;
    }

    // =========================================================================
    // LOOKUP METHODS - O(1) instead of O(n)
    // =========================================================================

    /**
     * Get passenger by booking reference
     * @param {string} reference - Booking reference (e.g., 'NUCO-12345')
     * @returns {Object|undefined} Passenger object or undefined
     */
    getByReference(reference) {
        return this.byReference.get(reference);
    }

    /**
     * Check if a reference exists
     * @param {string} reference
     * @returns {boolean}
     */
    hasReference(reference) {
        return this.byReference.has(reference);
    }

    /**
     * Get all passengers for an inbound transfer
     * @param {string} transferId
     * @returns {Array} Array of passengers (empty if none)
     */
    getByTransferInbound(transferId) {
        return this.byTransferInbound.get(transferId) || [];
    }

    /**
     * Get all passengers for an outbound transfer
     * @param {string} transferId
     * @returns {Array} Array of passengers (empty if none)
     */
    getByTransferOutbound(transferId) {
        return this.byTransferOutbound.get(transferId) || [];
    }

    /**
     * Get all passengers for a transfer (both directions)
     * @param {string} transferId
     * @returns {Object} { inbound: [], outbound: [] }
     */
    getByTransfer(transferId) {
        return {
            inbound: this.getByTransferInbound(transferId),
            outbound: this.getByTransferOutbound(transferId)
        };
    }

    /**
     * Get all passengers for a coach
     * @param {string} coachId
     * @returns {Array} Array of passengers
     */
    getByCoach(coachId) {
        return this.byCoach.get(coachId) || [];
    }

    /**
     * Get all passengers for an event
     * @param {string|number} eventId
     * @returns {Array} Array of passengers
     */
    getByEvent(eventId) {
        return this.byEvent.get(eventId) || [];
    }

    /**
     * Get all passengers for an accommodation
     * @param {string|number} subblockId
     * @returns {Array} Array of passengers
     */
    getByAccommodation(subblockId) {
        return this.byAccommodation.get(subblockId) || [];
    }

    /**
     * Get all passengers for a lesson
     * @param {string|number} lessonId
     * @returns {Array} Array of passengers
     */
    getByLesson(lessonId) {
        return this.byLesson.get(lessonId) || [];
    }

    // =========================================================================
    // SEARCH METHODS - Pre-lowercased for fast text search
    // =========================================================================

    /**
     * Search passengers by name or reference
     * Uses pre-lowercased strings for fast comparison (no allocation during search)
     *
     * @param {string} query - Search query
     * @param {Object} options - Search options
     * @param {number} options.limit - Maximum results to return (default: unlimited)
     * @param {string} options.brand - Filter by brand (optional)
     * @returns {Array} Array of matching passengers
     */
    search(query, options = {}) {
        if (!query || typeof query !== 'string') {
            return [];
        }

        const { limit = 0, brand = null } = options;
        const queryLower = query.toLowerCase();
        const results = [];
        const brandUpper = brand ? brand.toUpperCase() : null;

        for (const entry of this._searchIndex) {
            // Brand filter (if specified)
            if (brandUpper && entry.brand !== brandUpper && entry.brand !== 'WHITEROAD') {
                continue;
            }

            // Check name or reference match
            if (entry.nameLower.includes(queryLower) || entry.refLower.includes(queryLower)) {
                results.push(entry.passenger);

                // Early termination if limit reached
                if (limit > 0 && results.length >= limit) {
                    break;
                }
            }
        }

        return results;
    }

    /**
     * Get all passengers for a brand
     * @param {string} brand - Brand name (case-insensitive)
     * @returns {Array} Array of passengers
     */
    getByBrand(brand) {
        if (!brand) return [];
        return this.byBrand.get(brand.toUpperCase()) || [];
    }

    /**
     * Filter passengers by brand permission
     * Returns passengers matching the brand OR WHITEROAD (always visible)
     *
     * @param {string} brand - User's brand
     * @param {boolean} includeWhiteroad - Include WHITEROAD passengers (default: true)
     * @returns {Array} Filtered passengers
     */
    filterByBrandPermission(brand, includeWhiteroad = true) {
        if (!brand) return [];

        const brandUpper = brand.toUpperCase();
        const brandPassengers = this.byBrand.get(brandUpper) || [];

        if (includeWhiteroad && brandUpper !== 'WHITEROAD') {
            const whiteroadPassengers = this.byBrand.get('WHITEROAD') || [];
            return [...brandPassengers, ...whiteroadPassengers];
        }

        return brandPassengers;
    }

    /**
     * Search with brand permission filtering
     * Combines search with brand-based permission filtering
     *
     * @param {string} query - Search query
     * @param {string} brand - User's brand for filtering
     * @param {Object} options - Additional options (limit)
     * @returns {Array} Filtered search results
     */
    searchWithPermission(query, brand, options = {}) {
        return this.search(query, { ...options, brand });
    }

    /**
     * Benchmark search performance
     * @param {string} query - Search query to test
     * @param {number} iterations - Number of iterations
     * @returns {Object} Benchmark results
     */
    benchmarkSearch(query, iterations = 100) {
        if (!this._weekData || this._weekData.length === 0) {
            return { error: 'No data loaded' };
        }

        const results = {
            query,
            iterations,
            indexed: { totalMs: 0, avgMs: 0, resultCount: 0 },
            naive: { totalMs: 0, avgMs: 0, resultCount: 0 }
        };

        // Benchmark indexed search
        const startIndexed = performance.now();
        for (let i = 0; i < iterations; i++) {
            const r = this.search(query);
            if (i === 0) results.indexed.resultCount = r.length;
        }
        results.indexed.totalMs = performance.now() - startIndexed;
        results.indexed.avgMs = results.indexed.totalMs / iterations;

        // Benchmark naive search (recreates lowercase strings each time)
        const startNaive = performance.now();
        for (let i = 0; i < iterations; i++) {
            const queryLower = query.toLowerCase();
            const r = this._weekData.filter(p =>
                p.passengerName?.toLowerCase().includes(queryLower) ||
                p.reference?.toLowerCase().includes(queryLower)
            );
            if (i === 0) results.naive.resultCount = r.length;
        }
        results.naive.totalMs = performance.now() - startNaive;
        results.naive.avgMs = results.naive.totalMs / iterations;

        // Calculate speedup
        results.speedup = results.naive.totalMs / results.indexed.totalMs;

        console.log(
            `[PassengerIndex] Search Benchmark "${query}" (${iterations} iterations):\n` +
            `  Indexed: ${results.indexed.totalMs.toFixed(2)}ms total, ${results.indexed.avgMs.toFixed(2)}ms avg, ${results.indexed.resultCount} results\n` +
            `  Naive: ${results.naive.totalMs.toFixed(2)}ms total, ${results.naive.avgMs.toFixed(2)}ms avg, ${results.naive.resultCount} results\n` +
            `  Speedup: ${results.speedup.toFixed(1)}x faster`
        );

        return results;
    }

    // =========================================================================
    // BULK LOOKUP METHODS
    // =========================================================================

    /**
     * Get multiple passengers by references
     * @param {Array<string>} references - Array of booking references
     * @returns {Array} Array of passengers (preserves order, undefined for not found)
     */
    getByReferences(references) {
        return references.map(ref => this.byReference.get(ref));
    }

    /**
     * Get multiple passengers by references (only found ones)
     * @param {Array<string>} references
     * @returns {Array} Array of found passengers
     */
    getByReferencesFiltered(references) {
        return references
            .map(ref => this.byReference.get(ref))
            .filter(p => p !== undefined);
    }

    // =========================================================================
    // BENCHMARKING UTILITIES
    // =========================================================================

    /**
     * Benchmark indexed lookup vs array.find()
     * @param {string} reference - Reference to look up
     * @param {number} iterations - Number of iterations (default 1000)
     * @returns {Object} Benchmark results
     */
    benchmark(reference, iterations = 1000) {
        if (!this._weekData) {
            return { error: 'No data loaded' };
        }

        const results = {
            reference,
            iterations,
            indexed: { totalMs: 0, avgMs: 0, found: false },
            arrayFind: { totalMs: 0, avgMs: 0, found: false }
        };

        // Benchmark indexed lookup
        const startIndexed = performance.now();
        for (let i = 0; i < iterations; i++) {
            const p = this.byReference.get(reference);
            if (i === 0) results.indexed.found = !!p;
        }
        results.indexed.totalMs = performance.now() - startIndexed;
        results.indexed.avgMs = results.indexed.totalMs / iterations;

        // Benchmark array.find()
        const startFind = performance.now();
        for (let i = 0; i < iterations; i++) {
            const p = this._weekData.find(p => p.reference === reference);
            if (i === 0) results.arrayFind.found = !!p;
        }
        results.arrayFind.totalMs = performance.now() - startFind;
        results.arrayFind.avgMs = results.arrayFind.totalMs / iterations;

        // Calculate speedup
        results.speedup = results.arrayFind.totalMs / results.indexed.totalMs;

        console.log(
            `[PassengerIndex] Benchmark (${iterations} iterations):\n` +
            `  Indexed: ${results.indexed.totalMs.toFixed(2)}ms total, ${results.indexed.avgMs.toFixed(4)}ms avg\n` +
            `  Array.find(): ${results.arrayFind.totalMs.toFixed(2)}ms total, ${results.arrayFind.avgMs.toFixed(4)}ms avg\n` +
            `  Speedup: ${results.speedup.toFixed(1)}x faster`
        );

        return results;
    }

    /**
     * Benchmark with multiple random lookups
     * @param {number} lookupCount - Number of different references to look up
     * @param {number} iterations - Iterations per lookup
     * @returns {Object} Aggregate benchmark results
     */
    benchmarkRandom(lookupCount = 100, iterations = 100) {
        if (!this._weekData || this._weekData.length === 0) {
            return { error: 'No data loaded' };
        }

        // Pick random references
        const references = [];
        for (let i = 0; i < lookupCount; i++) {
            const randomIndex = Math.floor(Math.random() * this._weekData.length);
            references.push(this._weekData[randomIndex].reference);
        }

        const results = {
            lookupCount,
            iterations,
            totalLookups: lookupCount * iterations,
            indexed: { totalMs: 0, avgPerLookupMs: 0 },
            arrayFind: { totalMs: 0, avgPerLookupMs: 0 }
        };

        // Benchmark indexed lookups
        const startIndexed = performance.now();
        for (let i = 0; i < iterations; i++) {
            for (const ref of references) {
                this.byReference.get(ref);
            }
        }
        results.indexed.totalMs = performance.now() - startIndexed;
        results.indexed.avgPerLookupMs = results.indexed.totalMs / results.totalLookups;

        // Benchmark array.find() lookups
        const startFind = performance.now();
        for (let i = 0; i < iterations; i++) {
            for (const ref of references) {
                this._weekData.find(p => p.reference === ref);
            }
        }
        results.arrayFind.totalMs = performance.now() - startFind;
        results.arrayFind.avgPerLookupMs = results.arrayFind.totalMs / results.totalLookups;

        // Calculate speedup
        results.speedup = results.arrayFind.totalMs / results.indexed.totalMs;

        console.log(
            `[PassengerIndex] Random Benchmark (${lookupCount} refs × ${iterations} iterations = ${results.totalLookups} lookups):\n` +
            `  Indexed: ${results.indexed.totalMs.toFixed(2)}ms total, ${(results.indexed.avgPerLookupMs * 1000).toFixed(2)}µs per lookup\n` +
            `  Array.find(): ${results.arrayFind.totalMs.toFixed(2)}ms total, ${(results.arrayFind.avgPerLookupMs * 1000).toFixed(2)}µs per lookup\n` +
            `  Speedup: ${results.speedup.toFixed(1)}x faster`
        );

        return results;
    }

    /**
     * Get index statistics
     * @returns {Object} Statistics about the indexes
     */
    getStats() {
        return {
            passengerCount: this.passengerCount,
            buildDurationMs: this.buildDurationMs,
            lastBuildTime: this.lastBuildTime,
            indexes: {
                byReference: this.byReference.size,
                byTransferInbound: this.byTransferInbound.size,
                byTransferOutbound: this.byTransferOutbound.size,
                byCoach: this.byCoach.size,
                byEvent: this.byEvent.size,
                byAccommodation: this.byAccommodation.size,
                byLesson: this.byLesson.size,
                byBrand: this.byBrand.size,
                searchIndex: this._searchIndex.length
            }
        };
    }

    /**
     * Check if indexes are built and valid
     * @returns {boolean}
     */
    isReady() {
        return this.byReference.size > 0;
    }
}

// Export singleton instance
export const passengerIndex = new PassengerIndex();

// Export class for testing or custom instances
export { PassengerIndex };

// Expose to window for console debugging
if (typeof window !== 'undefined') {
    window.passengerIndex = passengerIndex;
}
